import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, useLocation, useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import AMLSummary from '../../../../components/AMLSummary/AMLSummary';
import Button from '../../../../components/Button';
import Icon, { Icons } from '../../../../components/Icon/Icon';
import InlineNotice from '../../../../components/InlineNotice';
import Input from '../../../../components/Input';
import ListItem from '../../../../components/ListItem';
import PageHeader from '../../../../components/PageHeader';
import QrScanner from '../../../../components/QrScanner';
import AppConfig from '../../../../config';
import { t } from '../../../../lang';
import RequestService from '../../../../services/RequestService';
import StorageService from '../../../../services/StorageService';
import TypesService from '../../../../services/TypesService';
import { appSlice, sendSlice } from '../../../../store/slices';

import './SendRecipient.scss';

const { routes, amlCheckEstimatedTimeInSec } = AppConfig;

const CurrenciesById = TypesService.CurrenciesById;
const BlockchainsById = TypesService.BlockchainsById;

const ERROR_ICON_SIZE = 20;
const USDT_TRC20_ID = 3;
const PROGRESS_STATUSES = ['Pending', 'Aborted', 'Queued', 'Requested'];

const SendRecipient = () => {
	const [addressIncorrenctError, setAddressIncorrenctError] = useState(false);
	const [addressEqualError, setAddressEqualError] = useState(false);
	const [isRecipientCorrect, setIsRecipientCorrect] = useState(false);
	const [leftTime, setLeftTime] = useState(amlCheckEstimatedTimeInSec);
	const [aml, setAml] = useState(null);
	const [isAddressRestrict, setAddressRestrict] = useState(false);

	const asset = useSelector(state => state.send.asset || {});
	const account = useSelector(state => state.send.account);
	const recipient = useSelector(state => state.send.recipient);
	const feeData = useSelector(state => state.send.feeData);
	const wsAmlResult = useSelector(store => store.app.wsAmlResult);

	const intl = useIntl();
	const location = useLocation();
	const navigate = useNavigate();
	const navigateType = useNavigationType();
	const dispatch = useDispatch();

	const [searchParams, setSearchParams] = useSearchParams();
	// const currencyId = searchParams.get('currencyId');
	const address = searchParams.get('address');
	const currencyData = CurrenciesById[asset.Id];
	const blockchainData = BlockchainsById[currencyData.BlockchainId];

	const checkRecipient = recipientAddr => {
		setAddressIncorrenctError(!!recipientAddr && !currencyData.Pattern.test(recipientAddr));
		setAddressEqualError(recipientAddr === account.Address);
	};

	const clearRecipient = () => {
		dispatch(sendSlice.actions.setRecipient(''));
		checkRecipient('');
	};

	const checkTransactionInProgress = () => {
		RequestService.GetActiveTopUpsList().then(response => {
			const setHaveActiveTransactionFromThisAddress = response?.TopUps?.some(
				topUp => topUp.BelongsToUsdtWithdrawal && topUp.WithdrawalData.SourceAddress === account.Address
			);
			dispatch(sendSlice.actions.setHaveActiveTransactionWarning(setHaveActiveTransactionFromThisAddress));
		});
	};

	const fetchFeeAddressData = async () => {
		try {
			const feeCurrencyId = Object.keys(TypesService.CurrenciesById).find(currencyId => {
				const currency = TypesService.CurrenciesById[currencyId];
				return currency.BlockchainId === currencyData.BlockchainId && !currency.Standart;
			});
			const numericFeeCurrencyId = Number(feeCurrencyId) || 0;
			const feeData = {
				CurrencyId: numericFeeCurrencyId,
				BlockchainId: CurrenciesById[feeCurrencyId].BlockchainId,
				Amount: 0,
				Fee: 0,
			};

			dispatch(sendSlice.actions.setFeeData(feeData));

			const addresses = await RequestService.GetAddressesByCurrency(numericFeeCurrencyId);
			if (addresses.Status === 'Success') {
				const comissionAddress = addresses.Addresses.find(({ Address }) => Address === account.Address);
				dispatch(sendSlice.actions.setFeeData({ ...feeData, Amount: comissionAddress.Amount }));
			}
		} catch (error) {
			console.log('error', error);
		}
	};

	useEffect(() => {
		if (navigateType === 'POP') return;
		clearRecipient();
		dispatch(sendSlice.actions.setAmount());
		dispatch(sendSlice.actions.setIsMaxAmountSetted());
		dispatch(sendSlice.actions.setFeeData());
		if (asset.Id === USDT_TRC20_ID) {
			checkTransactionInProgress();
			StorageService.UpdateTariffList();
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const isRecipientCorrect = currencyData.Pattern.test(recipient.trim()) && recipient !== account.Address;
		setIsRecipientCorrect(isRecipientCorrect);

		if (
			currencyData.FeeType === 'back_calculating' &&
			currencyData.Standart &&
			isRecipientCorrect &&
			!feeData.CurrencyId
		) {
			fetchFeeAddressData();
		}
	}, [recipient]); // eslint-disable-line react-hooks/exhaustive-deps

	const updateRecipient = e => {
		const recipient = e.target.value;
		dispatch(sendSlice.actions.setRecipient(recipient));
		setAddressIncorrenctError(false);
		setAddressEqualError(false);
	};

	const onBlurRecipient = e => {
		const recipient = e.target.value;
		const trimedRecipient = e.target.value.trim();

		if (recipient !== trimedRecipient) dispatch(sendSlice.actions.setRecipient(trimedRecipient));
		checkRecipient(trimedRecipient);
	};

	const goNextAmlShield = () => {
		dispatch(sendSlice.actions.setAmlShield(isAddressRestrict));

		navigate(routes.sendAmount, location);
	};

	const openAddressWarning = () => {
		dispatch(
			appSlice.actions.addModal({
				id: 'AddressWarningModal',
				isHeaderWithColorBgAndIcon: true,
				title: 'Address Warning',
				subtitle:
					'This address has restricted status. Some advanced transaction features may be unavailable if you proceed.',
				headerBackgroundColor: '#FF6161',
				headerIcon: 'XMARK_CIRCLE_FILL',
				tag: 'AddressWarningModal',
				isOpen: true,
				height: '70%',
				headClass: 'AddressWarningModal',
				outsideParams: {
					onConfirm: goNextAmlShield,
				},
			})
		);
	};

	const goNext = () => {
		// dispatch(sendSlice.actions.setAmlShield(isAddressRestrict));

		navigate(routes.sendAmount, location);
	};

	const pasteAddressValue = text => {
		const trimedText = text.trim();
		dispatch(sendSlice.actions.setRecipient(trimedText));
		checkRecipient(trimedText);
	};

	const onPaste = e => {
		const value = e.clipboardData?.getData('text/plain') || '';
		const trimedValue = value.trim();
		setTimeout(() => {
			dispatch(sendSlice.actions.setRecipient(trimedValue));
			checkRecipient(trimedValue);
		});
	};

	const error = addressIncorrenctError || addressEqualError;

	const checkAddressAmlClick = () => {
		setSearchParams({ address: recipient });

		navigate({
			pathname: routes.amlCheck,
			search: createSearchParams({ address: recipient, gotIt: 'toSendRecipient' }).toString(),
		});
	};

	useEffect(() => {
		address && dispatch(sendSlice.actions.setRecipient(address));
	}, [address]);

	useEffect(() => setLeftTime(amlCheckEstimatedTimeInSec), [amlCheckEstimatedTimeInSec]);

	useEffect(() => {
		// if (!PROGRESS_STATUSES.includes(aml?.Status)) return;

		const id = setInterval(() => {
			setLeftTime(prev => (prev <= 0 ? amlCheckEstimatedTimeInSec : prev - 1));
		}, 1000);

		return () => clearInterval(id);
	}, [amlCheckEstimatedTimeInSec, aml]);

	useEffect(() => {
		const isCorrectInput = currencyData.Pattern.test(recipient.trim()) && recipient !== account.Address;
		if (!isRecipientCorrect || !recipient.length || !isCorrectInput) return;

		const addressAmlDetails = async () => {
			const res = await RequestService.GetAddressAmlDetailsByAddress(recipient);
			setAml(res.AddressAmlDetails);
		};
		const restrictedAddressCheck = async () => {
			setAddressRestrict(Math.random() < 0.5);
		};

		addressAmlDetails();
		// restrictedAddressCheck();
	}, [isRecipientCorrect, recipient]);

	return (
		<div className='SendRecipient'>
			<PageHeader
				header={<>{currencyData.Name + `${currencyData.Standart && ' '}${currencyData.Standart}`}</>}
				icon={currencyData?.CompanyIcon}
				iconLabel={currencyData?.CompanyIcon !== blockchainData?.Icon ? blockchainData.Icon : null}
				collapsed
			/>
			<div className='SendRecipient__body'>
				<div className='SendRecipient__container'>
					<Input
						className='FormBlock'
						value={recipient}
						onChange={updateRecipient}
						onBlur={onBlurRecipient}
						placeholder={intl.formatMessage({ id: 'Enter address' })}
						error={addressIncorrenctError}
						prefix={t('To')}
						postfix={<QrScanner updateValue={pasteAddressValue} />}
						autoFocus={false}
						autoFocusOnClick={false}
						onPaste={onPaste}
						size={10}
					/>

					{/* {isRecipientCorrect && isAddressRestrict && <RestrictedAddressWarning />} */}

					{isRecipientCorrect && (!aml || aml?.Status === 'None') && (
						<div className='SendRecipient__aml'>
							<InlineNotice className='SendRecipient__aml_notice' type='warning' icon={'EXCLAMATIONMARK_CIRCLE'}>
								<p className='SendRecipient__aml__text'>
									{t('This address has not been AML-checked yet.\nPlease run an AML check before sending funds.')}
								</p>
							</InlineNotice>
							<Button iconBefore='AML_SCAN' styleType='green-dark' onClick={checkAddressAmlClick}>
								{t('Check address now')}
							</Button>
						</div>
					)}
					{isRecipientCorrect && aml?.Status === 'Success' && (
						<div className='SendRecipient__aml__progress'>
							<AMLSummary
								address={recipient}
								percent={aml?.RiskScore}
								blockchainName={aml?.Blockchain}
								isActualAML={aml?.IsActual}
								showBlockchain={true}
								highRisk={aml?.HighRisk}
								date={aml?.CheckDate}
								checkDate={aml?.CheckDate}
							/>
						</div>
					)}
					{isRecipientCorrect && PROGRESS_STATUSES.includes(aml?.Status) && (
						<div className='SendRecipient__aml__progress'>
							<AMLSummary
								isLoading={true}
								blockchainName={aml?.Blockchain}
								showBlockchain={true}
								date={aml?.CheckDate ? aml?.CheckDate : aml?.Created}
								bottomSeparator={true}
							/>
							<div className='SendRecipient__aml__progress__container'>
								<ListItem
									title={t('Estimated time')}
									trailing={[
										<div className='SendRecipient__aml__progress__trailing'>
											<div className='SendRecipient__aml__progress__time'>
												<p>{Math.floor(leftTime / 60)}</p>
												<p className='SendRecipient__aml__progress__time__symbol'>{t('m')}</p>
											</div>
											<div className='SendRecipient__aml__progress__time__separator'>:</div>
											<div className='SendRecipient__aml__progress__time'>
												<p>{String(leftTime % 60).padStart(2, '0')}</p>
												<p className='SendRecipient__aml__progress__time__symbol'>{t('s')}</p>
											</div>
										</div>,
									]}
								/>
							</div>
						</div>
					)}
					{isRecipientCorrect &&
						(aml?.Status === 'Failed' ||
							aml?.Status === 'Canceled' ||
							(!aml && (wsAmlResult?.Status === 'Failed' || wsAmlResult?.Status === 'Canceled'))) && (
							<div className='SendRecipient__aml'>
								<div className='SendRecipient__aml__progress'>
									<AMLSummary
										showBlockchain={true}
										isError={true}
										date={aml?.CheckDate ? aml?.CheckDate : aml?.Created}
									/>
								</div>
								<Button
									className='SendRecipient__aml__error__btn'
									styleType='green-dark'
									iconBefore={'ARROW_CLOCKWISE'}
									onClick={checkAddressAmlClick}
								>
									{t('Try again')}
								</Button>
							</div>
						)}
					{error && (
						<div className='SendRecipient__error-box'>
							<Icon type={Icons.XMARK_CIRCLE_FILL} width={ERROR_ICON_SIZE} height={ERROR_ICON_SIZE} />
							<span>
								{t(addressEqualError ? 'You cannot send funds to the same address' : 'The address incorrect')}
							</span>
						</div>
					)}
				</div>
				<Button onClick={goNext} disabled={!isRecipientCorrect}>
					{t('Next')}
				</Button>
				{/* <Button onClick={isAddressRestrict ? openAddressWarning : goNext} disabled={!isRecipientCorrect}>
					{t('Next')}
				</Button> */}
			</div>
		</div>
	);
};

export default SendRecipient;
